import React, { useRef, useEffect } from "react";
import {
    Box,
    useTheme,
    alpha,
} from '@material-ui/core';

import { useMeasure } from "react-use";
import { useCalculator } from '../../state/calculatorState';
import { dataHistoric, WPI, dataProjection } from '../../data/InflationRates'
import { MONTH_NAMES_SHORT } from "../../constants";
import { isUndefined } from "../../core/calculatorUtils"


const getPixelRatio = context => {
    let backingStore =
        context.backingStorePixelRatio ||
        context.webkitBackingStorePixelRatio ||
        context.mozBackingStorePixelRatio ||
        context.msBackingStorePixelRatio ||
        context.oBackingStorePixelRatio ||
        context.backingStorePixelRatio ||
        1;

    return (window.devicePixelRatio || 1) / backingStore;
};


export const InflationChart = () => {
    const ref = useRef();
    const theme = useTheme();
    const [{ income: salary },] = useCalculator();

    const [refContainer, { width }] = useMeasure();

    useEffect(() => {
        let canvas = ref.current;
        let context = canvas.getContext('2d');

        // resize the canvas to the parent node
        canvas.style.width = '100%';
        canvas.style.height = '100%';

        let ratio = getPixelRatio(context);
        let w = getComputedStyle(canvas)
            .getPropertyValue('width')
            .slice(0, -2);
        let h = getComputedStyle(canvas)
            .getPropertyValue('height')
            .slice(0, -2);

        // reset the new values
        canvas.width = w * ratio;
        canvas.height = h * ratio;

        const value = salary
        draw(context, canvas, theme, ratio, value);
    });


    let height = width === 0? 0 : 300;
    const bgTint = alpha(theme.palette.background.overlay, 0.3)

    return (
        <Box style={{ borderRadius: 10, padding: 20 }}>
            <Box style={{ padding: 0, paddingBottom: 0, position: "relative", backgroundColor: bgTint }}>
                <Box display="flex" flexDirection="column" flexGrow={1} style={{ height: `${height}px`, width: '100%' }} ref={refContainer}>
                    <canvas ref={ref} width={'100%'} height={`${height}px`} />
                </Box>
            </Box>
        </Box>
    );
}


const solveLinear = (min, max, valueMin, valueMax) => {
    const m = (max - min) / (valueMax - valueMin);
    const c = min - m * valueMin;
    return { m, c }
}

function dateToString(date) {
    return `${MONTH_NAMES_SHORT[date.getMonth()]} ${date.getFullYear()}`
}

function stringToDate(str) {
    // string in the form of MMM YYYY
    // eg Dec 2019
    return new Date(Date.parse(`1 ${str.toString()}`))

}
function stringToTime(str) {
    // string in the form of MMM YYYY
    // eg Dec 2019
    return new Date(Date.parse(`1 ${str.toString()}`)).getTime()

}


const draw = (ctx, canvas, theme, ratio, value) => {
    if( isUndefined(dataHistoric) || isUndefined(dataProjection) || isUndefined(WPI) ) return false

    let w = canvas.width;
    let h = canvas.height

    let margin = 30 * ratio;
    value = isFinite(value) ? value : 0;
    w = isFinite(w) ? w : 0;
    h = isFinite(h) ? h : 0;

    const cpiData = [];
    const wpiData = [];
    let smallScreen = w / ratio < 450
    let largeScreen = w / ratio > 800

    const fontSize = smallScreen ? 7*ratio : largeScreen ? 11 * ratio: 8 * ratio;

    // for small screens only show current year
    const count = dataHistoric.length;
    let startCount = smallScreen ? count - 10 : count - 14;
    startCount = largeScreen ? count - 12: startCount

    startCount = Math.max(0, startCount);

    for (var i = startCount; i < count; i++) {
        let d = dataHistoric[i]
        cpiData.push({
            ...d,
            prediction: false,
            current: (i === count - 1),
        })
        // CPI data and WPI data must be of the same length
        wpiData.push(WPI[i])
        
    }


    dataProjection.forEach((d) => {
        cpiData.push({
            ...d,
            prediction: true,
            current: false
        })
    })

    const last = cpiData[cpiData.length - 1]
    const startTime = stringToTime(cpiData[0].date)
    const startDate = stringToDate(cpiData[0].date)

    let endDate = stringToDate(last.date)
    // add 1 month
    endDate = new Date(endDate.setMonth(endDate.getMonth() + 6));

    const endTime = endDate.getTime()
    // extend last data point

    cpiData.push({
        ...last,
        date: dateToString(endDate),
        prediction: true,
        current: false
    })

    const startYear = startDate.getFullYear() + 1
    const endYear = endDate.getFullYear() + 1

    let min = Number.MAX_SAFE_INTEGER;
    let max = Number.MIN_SAFE_INTEGER;
    cpiData.forEach(d => {
        max = Math.max(max, d.rate)
        min = Math.min(min, d.rate)
    })

    let range = max - min;
    if (range === 0) range = 1;
    min = min - 1
    max = max + 1


    let graphTop = margin
    let graphBottom = h - margin;

    let graphLeft = margin
    let graphRight = w - margin;


    // BG
    const bg = alpha(theme.palette.background.overlay, 0.3)
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = `${bg}`;
    ctx.fillRect(0, 0, w, h);


    ctx.strokeStyle = alpha(theme.palette.primary.contrastText, 0.5);
    ctx.lineWidth = 1.0;

    // Title
    ctx.font = `${fontSize + 2}px ${theme.typography.h1.fontFamily[0]}`;
    ctx.fillStyle = theme.palette.primary.contrastText;
    ctx.textAlign = "left";
    const textX = graphLeft
    const textY = graphTop - 10 * ratio
    ctx.fillText(`INFLATION RATE`, textX, textY);

    // axis
    ctx.beginPath();
    ctx.moveTo(margin, margin)
    ctx.lineTo(margin, h - margin)
    ctx.lineTo(w - margin, h - margin);
    ctx.lineWidth = 1 * ratio;
    ctx.stroke()

    ctx.beginPath();
    const { m: my, c: cy } = solveLinear(graphBottom, graphTop, min, max)

    // y axis markers
    for (let i = 0; i < Math.ceil(max); i++) {
        let p = Math.round(i * my + cy);
        ctx.moveTo(graphLeft, p)
        ctx.lineTo(graphLeft - 5, p)

        ctx.font = `${fontSize}px ${theme.typography.h1.fontFamily[0]}`;
        ctx.fillStyle = theme.palette.text.primary;
        ctx.textAlign = "right";
        const textX = graphLeft - 7;
        const textY = p + 5
        ctx.fillText(`${i}%`, textX, textY);
    }

    const { m: mx, c: cx } = solveLinear(graphLeft, graphRight, startTime, endTime)

    for (let i = startYear; i < endYear; i++) {
        let time = new Date(i, 1, 1).getTime()
        // let time_ = new Date(i + 1, 1, 1).getTime()
        // let p_ = Math.round(time * mx + cx);
        let p = Math.round(time * mx + cx);
        // const midP = p - (p - p_)*0.5;
        let year = i
        ctx.moveTo(p, h - margin)
        ctx.lineTo(p, h - margin + 6)

        ctx.font = `${fontSize}px ${theme.typography.h1.fontFamily[0]}`;
        ctx.fillStyle = theme.palette.text.primary;
        ctx.textAlign = "center";
        ctx.fillText(Number(year), p, h - fontSize);
    }
    ctx.lineWidth = 1 * ratio;
    ctx.stroke()


    ctx.strokeStyle = alpha(theme.palette.primary.contrastText, 0.8);
    ctx.lineWidth = 4.0;
    ctx.beginPath();
    ctx.lineCap = "round"
    let prediction = false;
    let centerline = 0

    cpiData.forEach((d, index) => {
        // skip last as it is a placeholder only
        if (index < cpiData.length - 1) {

            let v = Math.round(d.rate * my + cy);
            let t = stringToTime(d.date)
            let t_ = stringToTime(cpiData[index + 1].date)
            let p = Math.round(t * mx + cx);
            let p_ = Math.round(t_ * mx + cx);
            const midP = p - (p - p_) * 0.5;

            // CURRENT
            if (d.current) {
                ctx.lineTo(p, v)
                ctx.stroke()
                ctx.strokeStyle = theme.palette.secondary.main;
                ctx.beginPath();
                //ctx.moveTo(p, v)
            }

            // SWITCH TO PREDICRION
            if (d.prediction && !prediction) {
                // switch to projected data
                ctx.stroke()

                ctx.beginPath();
                ctx.strokeStyle = alpha(theme.palette.primary.contrastText, 0.8);

                // dividing line goes here
                ctx.lineWidth = 1.0 * ratio;
                ctx.setLineDash([1 * ratio, 3 * ratio]);
                ctx.moveTo(p, graphTop)
                ctx.lineTo(p, graphBottom)
                ctx.stroke()
                centerline = p

                ctx.beginPath();
                ctx.fillStyle = alpha(theme.palette.background.default, 0.25);
                ctx.fillRect(graphTop, graphLeft, p - margin, graphBottom - graphTop - 1);


                // ctx.font = `${fontSize + 2}px ${theme.typography.h1.fontFamily[0]}`;
                // ctx.fillStyle = theme.palette.primary.main;
                // ctx.textAlign = "left";
                // const textX = p + 10 * ratio
                // const textY = graphBottom - margin
                // ctx.fillText(`RBA Prediction`, textX, textY);


                ctx.strokeStyle = theme.palette.primary.main;
                ctx.setLineDash([]);
                ctx.lineWidth = 4.0;
                ctx.beginPath();


                prediction = true;
                ctx.moveTo(p, v)
            }

            if (index === 0) {
                ctx.moveTo(p, v)
            } else {
                ctx.lineTo(p, v)
            }

            ctx.lineTo(p_, v)

            if (!smallScreen || d.current || d.prediction) {
                ctx.font = `${fontSize}px ${theme.typography.h1.fontFamily[0]}`;
                ctx.fillStyle = d.current ? theme.palette.secondary : theme.palette.text.primary;
                ctx.textAlign = "center";
                const textX = midP + 3 * ratio;
                const textY = v - 3 * ratio;
                
                if (d.current) {
                    ctx.font = `bold ${fontSize}px ${theme.typography.h1.fontFamily[0]}`;
                    ctx.fillStyle = theme.palette.secondary.main
                    ctx.fillText(`${d.rate}%`, textX, textY);

                    // ctx.textAlign = "left";
                    // const textX = midP + 10*ratio //p + 3*ratio
                    // const textY = v - 5 * ratio; //v + 15 * ratio;
                    // ctx.fillText(`Current`, textX - 40, textY - 30);
                    
                }else{
                    ctx.fillText(`${d.rate}%`, textX, textY);
                }
            }

        }
    })
    ctx.stroke()

    /// WPI
    ctx.beginPath();
    let wpi_data = {};
    let v,t,t_,p, p_ = 0
    wpiData.forEach((d, index) => {
        
        if(d){
            wpi_data = d
            v = Math.round(d.adjusted * my + cy);
            t = stringToTime(cpiData[index].date)
            t_ = stringToTime(cpiData[index + 1].date)
            p = Math.round(t * mx + cx);
            p_ = Math.round(t_ * mx + cx);
            if (index === 0) {
                ctx.moveTo(p, v)
            } else {
                ctx.lineTo(p, v)
            }
            ctx.lineTo(p_, v)
        }

        // add label to last index or if an undefined data point emerges
            if( index === wpiData.length - 1 || d === undefined){


                // extent line to current date
                ctx.lineTo(centerline, v)

                ctx.font = `${fontSize}px ${theme.typography.h1.fontFamily[0]}`;
                ctx.fillStyle = alpha(theme.palette.primary.contrastText, 0.8);
                ctx.textAlign = "right";
                const textX = largeScreen ? centerline + 32 * ratio: centerline + 25 * ratio
                const textY = v - 5 * ratio;

                ctx.fillText(`WPI ${wpi_data.adjusted}%`, textX, textY + 0*ratio);
                // ctx.fillText(`${wpi_data.adjusted}%`, textX, textY + 15*ratio);
                // ctx.fillText(`WPI`, textX, textY);
            }
        
    })
    ctx.lineWidth = 1.0*ratio
    ctx.setLineDash([2 * ratio, 3 * ratio]);
    ctx.strokeStyle = alpha(theme.palette.primary.contrastText, 0.5);
    ctx.stroke();


    // Key

    // axis
    if(smallScreen){
        ctx.beginPath();
        ctx.moveTo(w  - 95*ratio, 20*ratio)
        ctx.lineTo(w  - 75*ratio, 20*ratio)
        ctx.lineWidth = 3 * ratio;
        ctx.setLineDash([])
        ctx.strokeStyle = alpha(theme.palette.primary.contrastText, 0.8);
        ctx.stroke()
        ctx.textAlign = "left";
        ctx.fillText(`Consumer`, w - 65*ratio, 20*ratio);
        ctx.fillText(`Price Index`, w - 65*ratio, 32*ratio);
    
        ctx.beginPath();
        ctx.moveTo(w  - 95*ratio, 55*ratio)
        ctx.lineTo(w  - 75*ratio, 55*ratio)
        ctx.lineWidth = 3.0*ratio;
        ctx.strokeStyle = theme.palette.secondary.main;
        ctx.stroke()
        ctx.textAlign = "left";
        ctx.font = `${fontSize}px ${theme.typography.h1.fontFamily[0]}`;
        ctx.fillText(`Current`, w - 65*ratio, 53*ratio);
        ctx.fillText(`CPI`, w - 65*ratio, 65*ratio);

        ctx.beginPath();
        ctx.moveTo(w  - 95*ratio, 85*ratio)
        ctx.lineTo(w  - 75*ratio, 85*ratio)
        ctx.lineWidth = 3.0*ratio;
        ctx.strokeStyle = theme.palette.primary.main;
        ctx.stroke()
        ctx.textAlign = "left";
        ctx.font = `${fontSize}px ${theme.typography.h1.fontFamily[0]}`;
        ctx.fillText(`RBA`, w - 65*ratio, 83*ratio);
        ctx.fillText(`Prediction`, w - 65*ratio, 95*ratio);
    
        ctx.beginPath();
        ctx.moveTo(w  - 95*ratio, 115*ratio)
        ctx.lineTo(w  - 75*ratio, 115*ratio)
        ctx.lineWidth = 1.0*ratio;
        ctx.setLineDash([2 * ratio, 3 * ratio]);
        ctx.strokeStyle = alpha(theme.palette.primary.contrastText, 0.5);
        ctx.stroke()
        ctx.textAlign = "left";
        ctx.font = `${fontSize}px ${theme.typography.h1.fontFamily[0]}`;
        ctx.fillText(`Wage`, w - 65*ratio, 115*ratio);
        ctx.fillText(`Price Index`, w - 65*ratio, 129*ratio);

    }else{
        ctx.beginPath();
        ctx.moveTo(w  - 370*ratio, 20*ratio)
        ctx.lineTo(w  - 355*ratio, 20*ratio)
        ctx.lineWidth = 3 * ratio;
        ctx.setLineDash([])
        ctx.strokeStyle = alpha(theme.palette.primary.contrastText, 0.8);
        ctx.stroke()
        ctx.textAlign = "left";
        ctx.fillText(`Consumer`, w - 350*ratio, 20*ratio);
        ctx.fillText(`Price Index`, w - 350*ratio, 32*ratio);

        ctx.beginPath();
        ctx.moveTo(w  - 260*ratio, 20*ratio)
        ctx.lineTo(w  - 245*ratio, 20*ratio)
        ctx.lineWidth = 3 * ratio;
        ctx.setLineDash([])
        ctx.strokeStyle = alpha(theme.palette.secondary.main, 0.8);
        ctx.stroke()
        ctx.textAlign = "left";
        ctx.fillText(`Current`, w - 240*ratio, 20*ratio);
        ctx.fillText(`CPI`, w - 240*ratio, 32*ratio);

        ctx.beginPath();
        ctx.moveTo(w  - 170*ratio, 20*ratio)
        ctx.lineTo(w  - 155*ratio, 20*ratio)
        ctx.lineWidth = 3.0*ratio;
        ctx.strokeStyle = theme.palette.primary.main;
        ctx.stroke()
        ctx.textAlign = "left";
        ctx.font = `${fontSize}px ${theme.typography.h1.fontFamily[0]}`;
        ctx.fillText(`RBA`, w - 150*ratio, 20*ratio);
        ctx.fillText(`Prediction`, w - 150*ratio, 32*ratio);
    
    
    
        ctx.beginPath();
        ctx.moveTo(w  - 90*ratio, 20*ratio)
        ctx.lineTo(w  - 75*ratio, 20*ratio)
        ctx.lineWidth = 1.0*ratio;
        ctx.setLineDash([2 * ratio, 3 * ratio]);
        ctx.strokeStyle = alpha(theme.palette.primary.contrastText, 0.5);
        ctx.stroke()
    
        ctx.textAlign = "left";
        ctx.font = `${fontSize}px ${theme.typography.h1.fontFamily[0]}`;
        ctx.fillText(`Wage`, w - 70*ratio, 20*ratio);
        ctx.fillText(`Price Index`, w - 70*ratio, 32*ratio);
    }   
}

export default InflationChart